/** Small preferences kept on this device. Nothing secret is stored here. */
export interface DevicePrefs {
  micId?: string;
  camId?: string;
  speakerId?: string;
  micOn: boolean;
  camOn: boolean;
}

export type DataMode = 'auto' | 'dataSaver' | 'audioOnly';

export interface CallPrefs {
  /**
   * The person turned HD off. HD (720p) is otherwise sent in every meeting
   * whose plan includes it: it is what those plans are paid for. (Stored
   * under a new name: the old "hd" setting was off unless chosen, and was
   * written for everyone whenever another setting changed.)
   */
  hdOff: boolean;
  dataMode: DataMode;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<T>) } : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private browsing or a full quota: preferences simply are not remembered.
  }
}

export const devicePrefs = {
  get: (): DevicePrefs => read<DevicePrefs>('calling.devices', { micOn: true, camOn: true }),
  set: (patch: Partial<DevicePrefs>): void => write('calling.devices', { ...devicePrefs.get(), ...patch }),
};

export const callPrefs = {
  get: (): CallPrefs => read<CallPrefs>('calling.call', { hdOff: false, dataMode: 'auto' }),
  set: (patch: Partial<CallPrefs>): void => write('calling.call', { ...callPrefs.get(), ...patch }),
};

function text(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function setText(key: string, value: string): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* see write() */
  }
}

export const guestName = {
  get: (): string => text('calling.guestName'),
  set: (name: string): void => setText('calling.guestName', name),
};

/**
 * The guest session for a room. Keeping it means the host sees one request
 * from one person, even if the guest reloads the page.
 */
export const guestSession = {
  get: (code: string): string => text(`calling.guest.${code}`),
  set: (code: string, token: string): void => setText(`calling.guest.${code}`, token),
};

export const flags = {
  has: (name: string): boolean => text(`calling.flag.${name}`) === '1',
  set: (name: string): void => setText(`calling.flag.${name}`, '1'),
};
